import { useCallback, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconGripVertical } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import {
    createPdfFromPages,
    DEFAULT_CREATE_PDF_SETTINGS,
    expandFileToPages,
    isSupportedCreateFile,
} from './createPdfEngine';
import CreatePdfSettings from './CreatePdfSettings';
import CreatePdfPageGrid from './CreatePdfPageGrid';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfFileRow } from '../../components/pdf/PdfFileRow';
import { ImageFileRow } from '../../components/image/ImageFileRow';
import { isImageFile } from './createPdfEngine';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    createToolItemId,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ACCEPT = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/bmp',
    'image/avif',
    'image/heic',
    'image/heif',
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp',
    '.avif',
    '.heic',
    '.heif',
    '.hif',
].join(',');

function fileKey(file) {
    return `${file.name}::${file.size}::${file.lastModified}`;
}

function SortableFileRow({ file, onRemove, t }) {
    const id = fileKey(file);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const dragHandle = (
        <button
            type="button"
            className="cursor-grab border-none bg-transparent p-0 active:cursor-grabbing"
            aria-label={t('createPdf.reorder')}
            {...attributes}
            {...listeners}
        >
            <IconGripVertical size={16} />
        </button>
    );

    const rowProps = {
        name: file.name,
        meta: formatFileSize(file.size),
        onRemove: () => onRemove(file),
        removeLabel: t('createPdf.removeFile'),
        dragHandle,
    };

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.55 : 1,
            }}
        >
            {isImageFile(file) ? (
                <ImageFileRow {...rowProps} />
            ) : (
                <PdfFileRow {...rowProps} />
            )}
        </div>
    );
}

export default function CreatePdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [pages, setPages] = useState([]);
    const [settings, setSettings] = useState(DEFAULT_CREATE_PDF_SETTINGS);
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const sourceFiles = useMemo(() => {
        const map = new Map();
        for (const page of pages) {
            const key = fileKey(page.file);
            if (!map.has(key)) map.set(key, page.file);
        }
        return [...map.values()];
    }, [pages]);

    const activePageCount = useMemo(() => pages.filter((p) => !p.removed).length, [pages]);


    const invalidateResult = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    const addFiles = useCallback(async (incoming) => {
        const list = Array.from(incoming || []);
        const supported = list.filter(isSupportedCreateFile);
        if (!supported.length) {
            setError(t('createPdf.errors.invalidType'));
            return;
        }

        const newPages = [];
        for (const file of supported) {
            if (file.size > MAX_FILE_BYTES) {
                setError(t('createPdf.errors.tooLarge'));
                continue;
            }
            if (pages.some((p) => fileKey(p.file) === fileKey(file))) continue;

            try {
                const expanded = await expandFileToPages(file);
                newPages.push(...expanded.map((entry) => ({ ...entry, id: createToolItemId() })));
            } catch (err) {
                const msg = err?.message === 'password'
                    ? t('createPdf.errors.password')
                    : t('createPdf.errors.failed');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            }
        }

        if (newPages.length) {
            setPages((prev) => [...prev, ...newPages]);
            if (!selectedPageId) setSelectedPageId(newPages[0].id);
            invalidateResult();
        }
    }, [invalidateResult, pages, selectedPageId, sourceFiles.length, t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    usePdfPaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (file) => {
        const key = fileKey(file);
        setPages((prev) => {
            const next = prev.filter((p) => fileKey(p.file) !== key);
            setSelectedPageId((current) => {
                if (!next.length) return null;
                if (next.some((p) => p.id === current)) return current;
                return next[0].id;
            });
            return next;
        });
        invalidateResult();
    };

    const handleFileDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;

        const activeKey = String(active.id);
        const overKey = String(over.id);
        if (activeKey === overKey) return;

        setPages((prev) => {
            const activeFilePages = prev.filter((p) => fileKey(p.file) === activeKey);
            const overFilePages = prev.filter((p) => fileKey(p.file) === overKey);
            if (!activeFilePages.length || !overFilePages.length) return prev;

            const groups = [];
            const seen = new Set();
            for (const page of prev) {
                const key = fileKey(page.file);
                if (seen.has(key)) continue;
                seen.add(key);
                groups.push(prev.filter((p) => fileKey(p.file) === key));
            }

            const oldIndex = groups.findIndex((g) => fileKey(g[0].file) === activeKey);
            const newIndex = groups.findIndex((g) => fileKey(g[0].file) === overKey);
            const moved = arrayMove(groups, oldIndex, newIndex);
            return moved.flat();
        });
        invalidateResult();
    };

    const handleReset = () => {
        setPages([]);
        setSettings(DEFAULT_CREATE_PDF_SETTINGS);
        setSelectedPageId(null);
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const handleEditAgain = () => {
        setResult(null);
        setError(null);
        setProgress(null);
    };

    const handleSettingsChange = (next) => {
        setSettings(next);
        invalidateResult();
    };

    const handleCreate = async () => {
        if (!pages.length || processing) return;

        if (pages.every((p) => p.removed)) {
            const msg = t('createPdf.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        recordToolUsage('pdf/create', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const blob = await createPdfFromPages(pages, settings, (pageIndex, pageTotal) => {
                setProgress({ fileIndex: pageIndex, fileTotal: pageTotal });
            });

            const firstActive = pages.find((p) => !p.removed) || pages[0];
            const baseName = firstActive.file.name.replace(/\.[^.]+$/, '');
            setResult({
                name: `${baseName}-created.pdf`,
                size: blob.size,
                blob,
            });
            notifications.show({ title: t('common.success'), message: t('createPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('createPdf.errors.failed');
            if (err?.message === 'password') msg = t('createPdf.errors.password');
            else if (err?.message === 'unsupported') msg = t('createPdf.errors.invalidType');
            else if (err?.message === 'empty') msg = t('createPdf.errors.removeAll');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const totalInputSize = sourceFiles.reduce((sum, file) => sum + file.size, 0);

    const status = deriveToolStatus({
        hasInput: pages.length > 0,
        canRun: activePageCount > 0,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);
    const statusText = getToolStatusText(status, t, {
        empty: t('createPdf.previewEmpty'),
        ready: t('createPdf.previewReady', { count: toBnDigits(activePageCount) }),
        success: t('createPdf.done'),
    });

    const processingLabel = progress
        ? t('createPdf.progressPages', {
            page: toBnDigits(progress.fileIndex),
            pages: toBnDigits(progress.fileTotal),
        })
        : t('createPdf.creating');

    const successLabel = result
        ? t('createPdf.resultMeta', {
            files: toBnDigits(pages.length),
            size: formatFileSize(result.size),
        })
        : '';

    return (
        <ToolPageLayout
            slug="pdf/create"
            subtitleKey="createPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(sourceFiles.length) })}
            showAdd={sourceFiles.length > 0}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={pages.length > 0 || result || error ? handleReset : undefined}
            // From the first page on, the preview is exactly as tall as the left
            // column and scrolls inside — the finished PDF included, so a long
            // document never stretches the panel past the settings.
            matchSidebarHeight={pages.length > 0}
            // The finished PDF needs room for at least one whole page — the
            // settings column alone ends well above an A4 sheet's foot. It may run a
            // little past the fold; the action bar follows right under it.
            matchSidebarMinHeight={result ? 'min(1200px, calc(100vh + 120px))' : undefined}
            primaryAction={{
                label: t('createPdf.create'),
                onClick: handleCreate,
                disabled: !activePageCount || processing || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {sourceFiles.length === 0 ? (
                        <PdfDropZone
                            title={t('createPdf.dropTitle')}
                            hint={`${t('createPdf.dropHint')} ${t('createPdf.formats')}`}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <div className="space-y-2">
                            <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFileDragEnd}>
                                    <SortableContext items={sourceFiles.map(fileKey)} strategy={verticalListSortingStrategy}>
                                        {sourceFiles.map((file) => (
                                            <SortableFileRow
                                                key={fileKey(file)}
                                                file={file}
                                                onRemove={removeFile}
                                                t={t}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <p className="flex items-center gap-1.5 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" />
                                </svg>
                                {t('createPdf.orderHint')}
                            </p>
                            <p className="text-xs text-zinc-400">
                                {t('createPdf.fileCount', { count: toBnDigits(sourceFiles.length), size: formatFileSize(totalInputSize) })}
                                {' · '}
                                {t('createPdf.pageCount', { count: toBnDigits(pages.length) })}
                            </p>
                        </div>
                    )}
                </>
            )}
            settings={pages.length > 0 ? (
                <CreatePdfSettings
                    settings={settings}
                    onChange={handleSettingsChange}
                />
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('createPdf.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('createPdf.previewEmpty')}
                    idleHint={t('createPdf.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress ? Math.round((progress.fileIndex / progress.fileTotal) * 100) : undefined}
                    error={error}
                    successLabel={successLabel}
                    downloadLabel={t('createPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? handleEditAgain : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('createPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('createPdf.create')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={pages.length === 0 && !error}
                    // Always fill: the page grid and the finished PDF scroll inside the
                    // panel. The empty drop zone gets a fixed working height; once the
                    // panel is matched to the left column (desktop), that column decides.
                    fill
                    fillMinHeightClass={pages.length > 0
                        ? 'min-h-[min(560px,70vh)] lg:min-h-0'
                        : 'min-h-[min(560px,70vh)] lg:min-h-[700px]'}
                    tall={false}
                    pdfPreview={status === 'success' && !!result}
                >
                    {status === 'success' && result ? <PdfDocPreview source={result.blob} /> : null}
                    {status === 'ready' && pages.length > 0 ? (
                        <CreatePdfPageGrid
                            pages={pages}
                            selectedPageId={selectedPageId}
                            onPagesChange={(updater) => {
                                setPages(updater);
                                invalidateResult();
                            }}
                            onSelectPage={setSelectedPageId}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
